import React, { useContext, useEffect, useMemo } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { ColDef, RowNode, ValueFormatterParams } from "ag-grid-community";
import {
    campaignClassFormatter,
    formatCurrency,
    numericValueFormatter,
    titleCase,
} from "@ts/utils";
import { customFilterOptions } from "@ts/constants";
import {
    StatusBadgeCellRenderer,
    ChangeInValueCellRenderer,
    ProgramTypeCellRenderer,
    StatusCellRenderer,
    TagsCellRenderer,
    TargetCellRenderer,
    UpdateBidOrBudgetCellRenderer,
    FilterTextFormatter,
    PopOverNameCellRenderer,
    BiddingCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import { mainContext } from "@ts/constants";
import { useUpdateModule } from "@hooks/AllCustomHooks";
import { onCampaignSelectionChanged } from "@ts/performance-overview";
import { useSelector } from "react-redux";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import ActionCellRenderer from "@cell-renderers/ActionCellRenderer";
import {
    Channels,
    CostTypeText,
    IAdminToggleBtn,
    ICampaigns,
    IChannel,
    ICustomCellRendererParams,
    IMainContext,
    ParentModule,
    SubModule,
    TagColFields,
} from "@ts/common-types";
import { useGetModulesData } from "@hooks/AllCustomHooks";
import { Popover } from "antd";
import { ImInfo } from "react-icons/im";
import UpdateRequiredDailyBudgetModal from "@/components/modals/campaign-modal/UpdateRequiredDailyBudgetModal";
import CustomCampaignTagFilter from "@/components/filters/CustomCampaignTagFilter";
import BiddingModal from "@/components/modals/campaign-modal/BiddingModal";
import CustomTagTypeFilter from "@/components/filters/CustomTagTypeFilter";
import UpdateBidOrBudgetModal from "@/components/modals/UpdateBidOrBudgetModal";
import moment from "moment";

const Campaigns = React.memo(() => {
    const { campaignsLoading, campaignsGridApi, setCampaignsGridApi } =
        useContext<IMainContext>(mainContext);

    const campaignsData = useSelector(
        (state: ICampaigns) => state.campaignsData.value
    );
    const enableDayparting = useSelector(
        (state: IAdminToggleBtn) => state.enableDayparting.value
    );
    const channel = useSelector((state: IChannel) => state.channel.value);
    const updateBidOrBudgetModalDisplay = useSelector(
        (state: any) => state.updateBidOrBudgetModalDisplay.value
    );
    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.CAMPAIGN,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });
    const { display: biddingModalDisplay } = useSelector(
        (state: {
            biddingModal: {
                value: {
                    display: boolean;
                    data: Record<string, string | number>;
                };
            };
        }) => state.biddingModal.value
    );

    const endDateHeaderComponent = (params: any) => {
        const content = (
            <div
                className="text-center m-0"
                style={{ width: "10.8rem", height: "4.5rem", fontSize: "13px" }}
            >
                <p>
                    Click on the button corresponding to budget to apply the
                    date modifications
                </p>
            </div>
        );

        return (
            <>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="fw-bolder me-3">
                        {params.displayName.toUpperCase()}
                    </div>
                    {channel !== Channels.AMAZON ? (
                        <Popover
                            styles={{ body: { padding: "10px" } }}
                            content={content}
                        >
                            <ImInfo
                                style={{ cursor: "pointer", fontSize: "12px" }}
                            />
                        </Popover>
                    ) : null}
                </div>
            </>
        );
    };

    const colDefs: ColDef[] = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "campaign_name",
                headerName: "Campaign",
                filter: "agTextColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                checkboxSelection: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "campaign_name",
                cellRenderer: TargetCellRenderer,
                minWidth: gridParams["campaign_name"].minWidth,
                maxWidth: gridParams["campaign_name"].maxWidth,
                headerCheckboxSelection: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                    origin: "campaigns",
                    actions: "",
                    isBadgeRequired: !!gridParams?.isBadgeRequired,
                    isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
                    isCampaignSyncStatusRequired:
                        !!gridParams?.isCampaignSyncStatusRequired,
                },
            },
            {
                field: "start_date",
                headerName: "Start Date",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                maxWidth: 120,
                minWidth: 120,
                sort: "desc",
                filter: "agDateColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["date"],
                },
                valueFormatter: (params: ValueFormatterParams) =>
                    !params.node.rowPinned
                        ? params.value !== ""
                            ? moment(params.value, "YYYY-MM-DD").format(
                                  "DD-MM-YYYY"
                              )
                            : "NA"
                        : "",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "budget",
                headerName: "Budget",
                headerComponent: endDateHeaderComponent,
                filter: "agNumberColumnFilter",
                minWidth: gridParams["budget"].minWidth,
                maxWidth: gridParams["budget"].maxWidth,
                cellClass: "lock-pinned",
                pinned: "left",
                valueFormatter: (param: ValueFormatterParams) =>
                    !param.node.rowPinned
                        ? formatCurrency(param.value, param.data.country)
                        : "",
                cellRenderer: (params: ICustomCellRendererParams) =>
                    UpdateBidOrBudgetCellRenderer(params, "campaign"),
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isCampaignSyncStatusRequired:
                        !!gridParams?.isCampaignSyncStatusRequired,
                },
            },
            {
                field: "budget_type",
                headerName: "Budget Type",
                filter: "agSetColumnFilter",
                minWidth: 120,
                maxWidth: 130,
                cellClass: "lock-pinned",
                pinned: "left",
                hide: true,
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? titleCase(params.value) : "",
                filterParams: {
                    showTooltips: true,
                    valueFormatter: (params: ValueFormatterParams) =>
                        params.value ? titleCase(params.value) : "",
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "bidding",
                headerName: "Bidding",
                filter: "agNumberColumnFilter",
                minWidth: 100,
                maxWidth: 100,
                cellClass: "lock-pinned",
                pinned: "left",
                cellRenderer: BiddingCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "record_type",
                headerName: "Class",
                filter: "agSetColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                pinned: "left",
                lockPinned: true,
                cellClass: ["lock-pinned", "text-center"],
                valueFormatter: campaignClassFormatter,
                filterParams: {
                    showTooltips: true,
                    valueFormatter: campaignClassFormatter,
                    values: ["campaign"],
                },
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "portfolio_name",
                headerName: "Portfolio",
                filter: "agTextColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "automation",
                headerName: "Actions",
                maxWidth: 95,
                minWidth: 95,
                filter: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                cellStyle: { textAlign: "center" },
                cellRenderer: ActionCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isCampaignSyncStatusRequired:
                        !!gridParams?.isCampaignSyncStatusRequired,
                },
            },
            {
                field: "campaign_status",
                headerName: "Serving Status",
                filter: "agSetColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                pinned: "left",
                lockPinned: true,
                hide: true,
                maxWidth: 110,
                minWidth: 100,
                cellClass: ["lock-pinned", "text-center"],
                cellRenderer: StatusBadgeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isBadgeRequired: false,
                    isCampaignSyncStatusRequired:
                        !!gridParams?.isCampaignSyncStatusRequired,
                },
            },
            {
                field: "targetingtype",
                headerName: "Campaign Type",
                filter: "agSetColumnFilter",
                hide: true,
                maxWidth: 180,
                minWidth: 170,
                cellRenderer: ProgramTypeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    valueFormatter: FilterTextFormatter,
                },
            },
            {
                field: "state",
                headerName: "Status",
                maxWidth: 100,
                minWidth: 180,
                filter: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                cellStyle: { whiteSpace: "normal" },
                cellRenderer: (params: ICustomCellRendererParams) =>
                    StatusCellRenderer(params, "campaign"),
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isCampaignSyncStatusRequired:
                        !!gridParams?.isCampaignSyncStatusRequired,
                },
            },
            {
                field: "cost_type",
                headerName: "Bid Type",
                filter: "agSetColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                maxWidth: gridParams["cost_type"]?.maxWidth || 180,
                minWidth: gridParams["cost_type"]?.minWidth || 190,
                cellClass: "text-center",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    valueFormatter: FilterTextFormatter,
                },
                valueFormatter: (params: ValueFormatterParams) =>
                    CostTypeText[
                        params.data?.cost_type as keyof typeof CostTypeText
                    ],
            },
            {
                field: "program_type",
                headerName: "Ad Type",
                filter: "agSetColumnFilter",
                cellStyle: { whiteSpace: "normal" },
                maxWidth: gridParams["program_type"].maxWidth,
                minWidth: gridParams["program_type"].minWidth,
                cellClass: "text-center",
                cellRenderer: ProgramTypeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filterParams: {
                    valueFormatter: FilterTextFormatter,
                },
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? params.value.toUpperCase() : "",
            },
            {
                field: "creation_date",
                headerName: "Creation Date",
                maxWidth: 150,
                minWidth: 150,
                filter: "agDateColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["date"],
                },
                valueFormatter: (params: ValueFormatterParams) => {
                    if (params.node.rowPinned) return "";
                    const isValidEpoch =
                        moment.unix(params.value).isValid() &&
                        String(params.value).length === 10;
                    if (!isValidEpoch) return "N/A";
                    return moment.unix(params.value).format("DD-MM-YYYY");
                },
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                headerName: "Spends",
                cellStyle: { whiteSpace: "normal" },
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                minWidth: "170",
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "ACoS",
                cellStyle: { whiteSpace: "normal" },
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Ad Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "troas",
                headerName: "TRoAS",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CTR",
                cellStyle: { whiteSpace: "normal" },
                field: "ctr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                headerName: "Impressions",
                cellStyle: { whiteSpace: "normal" },
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                // aggFunc: "sum",
            },
            {
                headerName: gridParams["click"]?.headerName || "Clicks",
                cellStyle: { whiteSpace: "normal" },
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
            },
            {
                headerName: "AOV",
                cellStyle: { whiteSpace: "normal" },
                field: "aov",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                    hideInfinite: true,
                },
            },
            {
                headerName: "Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
            },
            {
                headerName: "Indirect Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_view_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Orders",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_units",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: gridParams["cpc"]?.headerName || "CPC",
                cellStyle: { whiteSpace: "normal" },
                field: "cpc",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "CPM",
                field: "cpm",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CR",
                cellStyle: { whiteSpace: "normal" },
                field: "cr",
                filter: "agNumberColumnFilter",
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "CPA",
                cellStyle: { whiteSpace: "normal" },
                field: "cpa",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "RoAS",
                cellStyle: { whiteSpace: "normal" },
                field: "roas",
                filter: "agNumberColumnFilter",
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Top ASIN",
                cellStyle: { whiteSpace: "normal" },
                field: "top_product",
                filter: "agSetColumnFilter",
                hide: true,
                // pinnedRowCellRenderer: blankCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                headerName: "% Spend",
                cellStyle: { whiteSpace: "normal" },
                field: "percent_spend",
                hide: true,
            },
            {
                field: "sys_campaign_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "origin",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "portfolio_id",
                filter: "agSetColumnFilter",
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "percent_utilisation",
                headerName: "Percent Utilisation",
                filter: "agNumberColumnFilter",
                minWidth: 115,
                maxWidth: 115,
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: false,
                    decimalPlace: 2,
                    hasSingleValue: true,
                },
            },
            {
                field: "tag_types",
                headerName: "Tag Types",
                filter: CustomTagTypeFilter,
                filterParams: {
                    tagMappingField: TagColFields.CAMPAIGN_TAGS,
                },
                minWidth: 200,
                maxWidth: 250,
                cellRenderer: PopOverNameCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    tagMappingField: TagColFields.CAMPAIGN_TAGS,
                },
                hide: true,
            },
            {
                field: TagColFields.CAMPAIGN_TAGS,
                headerName: "Campaign Tags",
                filter: CustomCampaignTagFilter,
                minWidth: 200,
                cellRenderer: TagsCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "marketplace_type",
                headerName: "Marketplace Type",
                valueFormatter: FilterTextFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                hide: true,
                filter: "agSetColumnFilter",
                filterParams: {
                    showTooltips: true,
                    valueFormatter: FilterTextFormatter,
                },
            },
            {
                headerName: "Budget Utilization",
                cellStyle: { whiteSpace: "normal" },
                field: "budget_utilization",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: 150,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: false,
                },
            },
            {
                headerName: "Uptime",
                cellStyle: { whiteSpace: "normal" },
                field: "campaign_uptime",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: 120,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: false,
                },
            },
        ],
        activeModuleName: SubModule.CAMPAIGN,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });

    const campaignsColumnDefs = useMemo(
        () =>
            !enableDayparting
                ? colDefs.filter(
                      (data: ColDef) => data.headerName !== "Actions"
                  )
                : colDefs,
        [enableDayparting, colDefs]
    );

    const updateModule = useUpdateModule(SubModule.CAMPAIGN);
    useEffect(() => {
        sessionStorage.setItem("trendType", "campaigns");
    }, []);

    const isRowSelectable = useMemo(() => {
        return (rowNode: RowNode) => {
            if (!rowNode?.data) return false;

            if (rowNode.data.is_archived_entity) {
                return (
                    gridParams?.isArchivedCampaignsSelectionAllowed &&
                    rowNode.data.campaign_status !== "ended"
                );
            } else {
                return true;
            }
        };
    }, [gridParams]);

    return (
        <>
            <div className="wrapper" style={{ flex: "1 1 0" }}>
                <div
                    id="campaigns-grid"
                    className="ag-theme-alpine"
                    style={{ height: "100%", width: "100%" }}
                >
                    <AgGridLayout
                        rowData={campaignsData}
                        columnDefs={campaignsColumnDefs}
                        setGridApi={setCampaignsGridApi}
                        onSelectionChanged={onCampaignSelectionChanged}
                        updateModule={updateModule}
                        loading={campaignsLoading}
                        isTotalRowsReq={true}
                        rowHeight={gridParams?.rowHeight}
                        isRowSelectable={isRowSelectable}
                    />
                </div>
                <AddRulesModal api={campaignsGridApi} />
            </div>
            <UpdateRequiredDailyBudgetModal />
            {biddingModalDisplay && <BiddingModal />}
            {updateBidOrBudgetModalDisplay && (
                <UpdateBidOrBudgetModal type="budget" />
            )}
        </>
    );
});

export default Campaigns;
